import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, ImageIcon, MonitorPlay, Mail, Tv } from 'lucide-react';
import WebcastSection from './WebcastSection';
import FlyersSection from './FlyersSection';
import DepartmentEmailModule from './DepartmentEmailModule';
import DisplayManagerModule from './DisplayManagerModule';

const MEDIA_TABS = [
  { id: 'display', label: 'Live Display', icon: Tv, color: 'from-purple-500 to-indigo-500' },
  { id: 'webcast', label: 'Webcast', icon: Video, color: 'from-red-500 to-rose-500' },
  { id: 'flyers', label: 'Flyers', icon: ImageIcon, color: 'from-amber-500 to-orange-500' },
  { id: 'email', label: 'Email', icon: Mail, color: 'from-blue-500 to-indigo-500' }
];

const MediaDepartmentSection = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('display');

  return (
    <div className="space-y-6" data-testid="media-department-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-red-500 via-rose-500 to-orange-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white shadow-xl"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="p-3 rounded-xl bg-white/20 backdrop-blur-md w-fit">
            <MonitorPlay className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold">Media Department</h2>
            <p className="text-red-100 text-sm sm:text-base">Manage church displays, webcasts, and flyers</p>
          </div>
        </div>
      </motion.div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto">
          {MEDIA_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'display' && (
          <motion.div
            key="display"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DisplayManagerModule />
          </motion.div>
        )}
        {activeTab === 'webcast' && (
          <motion.div
            key="webcast"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <WebcastSection adminUser={adminUser} />
          </motion.div>
        )}
        {activeTab === 'flyers' && (
          <motion.div
            key="flyers"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <FlyersSection />
          </motion.div>
        )}
        {activeTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DepartmentEmailModule department="media" adminUser={adminUser} compact={false} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaDepartmentSection;
